import { collection, doc, onSnapshot, query, where, type DocumentData } from "firebase/firestore";
import { useEffect, useState, useSyncExternalStore } from "react";
import { firebaseDienste, firestoreBeobachten, firestoreGeneration } from "./firebase";
import { useSitzung } from "./sitzung";

/**
 * Lesemodelle über Firestore-Listener (ADR-0010, lesemodelle.md §1): Tabellensammlungen immer mit
 * `_leser array-contains <uid>`, sonst verweigern die Sicherheitsregeln die Abfrage. Sortiert wird nach `_reihenfolge`
 * im Gerät, damit keine zusammengesetzten Indizes nötig sind. Startet Firestore neu (Beginn oder Ende des Jagdtags,
 * plattform/offline.ts), melden sich die Listener an der neuen Instanz wieder an.
 */
export interface Lesestand<T> {
  daten: T;
  laedt: boolean;
  fehler: string | null;
  /** Pfad, zu dem die Daten gehören */
  pfad?: string | null;
}

export type Zeile = DocumentData & { _id: string };

/** Zeitstempel → Date, rekursiv (auch in Listen, z. B. `Jagdzeitplan` [{Beginn, Ende}]). */
function wert(v: unknown): unknown {
  if (v && typeof v === "object" && typeof (v as { toDate?: unknown }).toDate === "function") return (v as { toDate(): Date }).toDate();
  if (Array.isArray(v)) return v.map(wert);
  if (v && typeof v === "object") return werte(v as DocumentData);
  return v;
}

function werte(d: DocumentData): DocumentData {
  const aus: DocumentData = {};
  for (const [k, v] of Object.entries(d)) aus[k] = wert(v);
  return aus;
}

/**
 * Zuletzt gelesene Stände je Pfad, nur im Arbeitsspeicher des Fensters: Eine Ansicht, die neu geöffnet wird, zeigt sie,
 * bis ihr Listener liefert (auch während Firestore neu startet). `lesemodelleVergessen` leert sie, wenn die Daten der
 * Drückjagd das Gerät verlassen (plattform/offline.ts).
 */
const zuletzt = new Map<string, Lesestand<unknown>>();

export function lesemodelleVergessen(): void {
  zuletzt.clear();
}

function anfang<T>(art: string, pfad: string | null, leer: T): Lesestand<T> {
  const z = pfad ? (zuletzt.get(`${art}:${pfad}`) as Lesestand<T> | undefined) : undefined;
  return z ? { ...z, laedt: true } : { daten: leer, laedt: true, fehler: null, pfad };
}

/**
 * Liefert der Cache ohne Netz „nichts“ (z. B. nachdem der Browser den Speicher geräumt hat), weiß das Gerät nur nicht,
 * was es gibt: Hat dieses Fenster den Stand schon gelesen, bleibt er stehen, bis wieder Netz besteht. Gelöscht wird er
 * nur, wenn die Daten das Gerät verlassen müssen (`lesemodelleVergessen`, plattform/offline.ts).
 */
function ohneNetzUnbekannt(ausCache: boolean, schluessel: string): boolean {
  if (!ausCache || navigator.onLine) return false;
  const bekannt = zuletzt.get(schluessel)?.daten;
  return Array.isArray(bekannt) ? bekannt.length > 0 : !!bekannt;
}

/**
 * Fehler eines Listeners: Ohne Leserecht (Zugang entzogen) verschwinden die Daten; beendet sich nur die Instanz (der
 * Browser räumt den Speicher, Firestore startet neu), bleibt der zuletzt gelesene Stand stehen.
 */
function fehlerstand<T>(s: Lesestand<T>, code: string, leer: T, pfad: string): Lesestand<T> {
  if (code === "permission-denied") return { daten: leer, laedt: false, fehler: code, pfad };
  return { ...s, laedt: false, fehler: code, pfad };
}

/** Zähler der Firestore-Instanz als Abhängigkeit der Listener. */
export function useFirestoreGeneration(): number {
  return useSyncExternalStore(firestoreBeobachten, firestoreGeneration);
}

export function useDokument(pfad: string | null): Lesestand<Zeile | null> {
  const { uid } = useSitzung();
  const generation = useFirestoreGeneration();
  const [stand, setStand] = useState<Lesestand<Zeile | null>>(() => anfang("d", pfad, null));
  useEffect(() => {
    if (!pfad || !uid) {
      setStand({ daten: null, laedt: false, fehler: null });
      return;
    }
    // Neustart von Firestore (gleicher Pfad): die bisherigen Daten bleiben stehen, bis die neue Instanz liefert — außer
    // die Daten mussten das Gerät verlassen (`lesemodelleVergessen`).
    setStand((s) => (s.pfad === pfad && zuletzt.has(`d:${pfad}`) ? { ...s, laedt: true } : anfang("d", pfad, null)));
    const setzen = (neu: Lesestand<Zeile | null>) => { if (!neu.fehler) zuletzt.set(`d:${pfad}`, neu); setStand(neu); };
    try {
      return onSnapshot(doc(firebaseDienste().db, pfad),
        (snap) => {
          if (!snap.exists() && ohneNetzUnbekannt(snap.metadata.fromCache, `d:${pfad}`)) return;
          setzen({ daten: snap.exists() ? { ...werte(snap.data()), _id: snap.id } : null, laedt: false, fehler: null, pfad });
        },
        (e) => setStand((s) => fehlerstand(s, e.code, null, pfad)));
    } catch {
      return undefined; // Firestore beendet (Speicher geleert): der Neustart meldet den Listener wieder an
    }
  }, [pfad, uid, generation]);
  return stand;
}

export function useSammlung(pfad: string | null): Lesestand<Zeile[]> {
  const { uid } = useSitzung();
  const generation = useFirestoreGeneration();
  const [stand, setStand] = useState<Lesestand<Zeile[]>>(() => anfang("s", pfad, []));
  useEffect(() => {
    if (!pfad || !uid) {
      setStand({ daten: [], laedt: false, fehler: null });
      return;
    }
    setStand((s) => (s.pfad === pfad && zuletzt.has(`s:${pfad}`) ? { ...s, laedt: true } : anfang("s", pfad, [])));
    const setzen = (neu: Lesestand<Zeile[]>) => { if (!neu.fehler) zuletzt.set(`s:${pfad}`, neu); setStand(neu); };
    try {
      const q = query(collection(firebaseDienste().db, pfad), where("_leser", "array-contains", uid));
      return onSnapshot(q, (snap) => {
        if (snap.empty && ohneNetzUnbekannt(snap.metadata.fromCache, `s:${pfad}`)) return;
        const zeilen: Zeile[] = snap.docs.map((d) => ({ ...werte(d.data()), _id: d.id }));
        zeilen.sort((a, b) => Number(a["_reihenfolge"] ?? 0) - Number(b["_reihenfolge"] ?? 0));
        setzen({ daten: zeilen, laedt: false, fehler: null, pfad });
      }, (e) => setStand((s) => fehlerstand(s, e.code, [] as Zeile[], pfad)));
    } catch {
      return undefined; // Firestore beendet (Speicher geleert): der Neustart meldet den Listener wieder an
    }
  }, [pfad, uid, generation]);
  return stand;
}
